import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { t } from 'i18next';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ReactMarkdown from 'react-markdown';
import { ChatbotKpiWrapper } from 'components/chatbot/ChatbotKpiWrapper';
import { ChatAccordion } from 'components/chatbot/ChatAccordion';
import CopyIcon from 'assets/images/icons/copy-icon.svg';
import EditIcon from 'assets/images/icons/edit-icon.svg';
import RetryIcon from 'assets/images/icons/retry-icon.svg';
import LikeIcon from 'assets/images/icons/like-icon.svg';
import UnlikeIcon from 'assets/images/icons/unlike-icon.svg';
import LikeIconFilled from 'assets/images/icons/like-icon-filled.svg';
import UnlikeIconFilled from 'assets/images/icons/unlike-icon-filled.svg';
import { ChatFeedback } from './ChatFeedback';
import { submitFeedbackService } from 'reduxLib/services/chatBotService';
import { checkAndFormatDate, isCurrencyField } from 'helpers/lisaHelpers';

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        maxHeight: '300px',
        width: '80%',
        margin: `${theme.spacing(2)}px ${theme.spacing(3)}px ${theme.spacing(1)}px`,
        boxShadow: '0px 0px 1px black',
    },
    tableHead: {
        position: 'sticky',
        top: '-1px',
        left: 0,
    },
    tableCellHeader: {
        fontWeight: 'bold',
        fontSize: theme.spacing(2),
        border: '1px solid #aaf',
        textTransform: 'capitalize',
        width: `${theme.spacing(2.5)}%`,
        padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    },
    tableCell: {
        fontSize: theme.spacing(2),
        padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
        width: `${theme.spacing(2.5)}%`,
        color: theme.palette.common.black,
        border: '1px solid #aaf'
    },
    tableFooterExt: {
        width: '80%',
        textAlign: 'right',
        fontSize: theme.spacing(1.875),
        fontWeight: 'bold',
        textTransform: 'capitalize',
    },
    botMessage: {
        marginBottom: theme.spacing(7.5),
    },
    confidenceLabel: {
        // marginBottom: theme.spacing(2),
    },
    confidenceLegend: {
        display: 'inline-block',
        width: theme.spacing(2),
        height: theme.spacing(2),
        borderRadius: '50%',
        verticalAlign: 'text-bottom'
    },
    msgActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        justifySelf: 'end',
        position: 'relative',
        width: 'auto',
        bottom: theme.spacing(-1.75),
        padding: theme.spacing(0, 1, 0),
        alignItems: 'center',
        "&.qstnActions": {
            bottom: 0,
            top: theme.spacing(0.25),
            right: theme.spacing(1.25)
        }
    },
    msgCompletedActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        position: 'relative',
        bottom: theme.spacing(3),
        height: 0,
        '& button': {
            height: theme.spacing(3.5),
            border: `1px solid #aaa`,
            borderRadius: theme.spacing(2.5),
            padding: theme.spacing(0.25, 0.5),
            backgroundColor: theme.palette.white
        }
    },
    noHeight: {
        height: '0px;'
    },
    msgActionBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        // margin: theme.spacing(0, 1),
        // '&.active': {
        //     borderBottom: '3px solid black'
        // },
        padding: theme.spacing(1.25),
        borderRadius: theme.spacing(2.5),
        "&:hover": {
            backgroundColor: "#eee",
        },
        '& img': {
            width: theme.spacing(3),
            height: theme.spacing(3),
        }
    },
    finalAnswer: {
        marginBottom: theme.spacing(2)
    },
    accordionTitle: {
        fontSize: theme.spacing(2)
    },
    queryFilters: {
        fontSize: theme.spacing(2)
    },
    qryFilterName: {
        fontWeight: "bold",
        paddingRight: theme.spacing(1.5),
        paddingLeft: theme.spacing(2),
        wordWrap: 'normal',
        textTransform: 'capitalize'
    },
    contextQuestion: {
        paddingLeft: theme.spacing(2),
        wordWrap: 'normal',
        paddingBottom: theme.spacing(1),
    },
    qryFilterValue: {
        paddingBottom: theme.spacing(1),
    },
    qryFilterHeading: {
        fontSize: theme.spacing(2),
        fontWeight: "bold",
        paddingTop: theme.spacing(1.5),
    },
    responseTime: {
        fontSize: theme.spacing(2),
        paddingTop: theme.spacing(2),
        color: '#555'
    }
}));

export const ChatResponse = ({ message, tableData, chartProps, ...rest }) => {
    const classes = useStyles();
    const [isMsgCopied, setIsMsgCopied] = useState(false);
    const [feedbackFlag, setFeedbackFlag] = useState(null);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

    // const understandingLogic = { test: "test" }
    const queryFilters = message?.query_filters ?? {};
    const calculations = message?.Calculations ?? {};
    const questionUid = rest?.metadata?.question_uuid ?? "";
    const contextualizedQuestion = rest?.metadata?.contextualized_question;

    useEffect(() => {
        if (isMsgCopied) {
            setTimeout(() => {
                setIsMsgCopied(false)
            }, 3000)
        }
    }, [isMsgCopied])

    const dataKeys = tableData && tableData.length ? Object.keys(tableData[0]) : [];

    const formatValue = (key, value) => {
        if (isCurrencyField(key, value)) {
            const dollarVal = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
            }).format(value);
            return dollarVal.split('.')[0]
        }
        return checkAndFormatDate(value)
    }

    const copyMessage = () => {
        let copyText = message?.final_answer
        if (contextualizedQuestion || Object.keys(queryFilters).length > 0 || Object.keys(calculations).length > 0) {
            copyText += `\n\n ${t("retrieval_details")}:`;

            if (contextualizedQuestion) {
                copyText += `\n\n ${t("question_interpreted_as")}:`;
                copyText += `\n ${contextualizedQuestion}`;
            }

            if (Object.keys(calculations).length > 0) {
                copyText += `\n\n ${t("calculations")}:`;
                Object.keys(calculations).forEach(filterKey => {
                    copyText += `\n ${filterKey.replaceAll("_", " ")}: ${calculations[filterKey]}`;
                })
            }

            if (Object.keys(queryFilters).length > 0) {
                copyText += `\n\n ${t("filters_applied")}:`;
                Object.keys(queryFilters).forEach(filterKey => {
                    copyText += `\n ${filterKey.replaceAll("_", " ")}: ${queryFilters[filterKey]?.join(", ")}`;
                })
            }

        }

        navigator.clipboard.writeText(copyText)
            .then(() => {
                setIsMsgCopied(true)
            })
            .catch(err => {
                console.log("Failed to copy: ", err);
            });
    }

    const likeMessage = () => {
        if (feedbackFlag === 1) {
            neutralFeedback();
            return;
        }
        setFeedbackFlag(1);
        submitFeedbackService({
            region: rest?.region,
            user: rest?.user,
            questionUid: questionUid,
            feedbackText: "",
            feedbackFlag: 1,
        })
    }
    const unlikeMessage = () => {
        if (feedbackFlag === -1) {
            neutralFeedback();
            return;
        }
        setIsFeedbackOpen(true);
    }
    const neutralFeedback = () => {
        setFeedbackFlag(0);
        submitFeedbackService({
            region: rest?.region,
            user: rest?.user,
            questionUid: questionUid,
            feedbackText: "",
            feedbackFlag: 0,
        })
    }

    const submitFeedback = async (feedbackText) => {
        setFeedbackFlag(-1);
        const result = await submitFeedbackService({
            region: rest?.region,
            user: rest?.user,
            questionUid: questionUid,
            feedbackText,
            feedbackFlag: -1,
        })
    }

    const closeFeedback = () => {
        setIsFeedbackOpen(false)
    }

    const editMessage = () => {
        // chat input is not accessible by react
        const input = document.querySelector('.rcb-chat-input-textarea');
        input.value = rest?.params?.userInput;
        input.focus();
    }

    const resendMessage = () => {
        // chat input is not accessible by react
        const input = document.querySelector('.rcb-chat-input-textarea');
        input.value = rest?.params?.userInput;
        const enterEvent = new KeyboardEvent("keydown", {
            key: "Enter",
            code: "Enter",
            keyCode: 13,
            which: 13,
            bubbles: true
        });
        input.dispatchEvent(enterEvent);
    }

    const getAccordionItems = () => {
        const accordionList = [];

        // if (Object.keys(understandingLogic ?? {}).length > 0) {
        //     accordionList.push({
        //         title: <small>{t('how_i_understand_your_question')}</small>,
        //         content: <small>
        //             <p>Primary Interpretation </p>
        //             <p>Alternate Interpretation  </p>
        //         </small>
        //     })
        // }

        if (Object.keys(queryFilters).length > 0 || Object.keys(calculations).length > 0) {
            accordionList.push({
                title: <b className={`${classes.accordionTitle} accordionTitle`}>{t("retrieval_details")}</b>,
                content: <table className={classes.queryFilters}>
                    {/* Question interpretation */}
                    {contextualizedQuestion && <>
                        <tr>
                            <td className={`${classes.qryFilterHeading} qryFilterHeading`} colSpan={2}>
                                {t('question_interpreted_as')}:
                            </td>
                        </tr>
                        <tr>
                            <td className={classes.contextQuestion} colSpan={2}>{contextualizedQuestion}</td>
                        </tr>

                    </>}
                    {/* Calculations */}
                    {Object.keys(calculations).length > 0 &&
                        <>
                            <tr>
                                <td className={`${classes.qryFilterHeading} qryFilterHeading`} colSpan={2}>{t('calculations')}:</td>
                            </tr>
                            {
                                Object.keys(message.Calculations).map(key => {
                                    return (
                                        <tr key={key}>
                                            <td className={classes.qryFilterName}>{key.replaceAll("_", " ")}:</td>
                                            <td className={classes.qryFilterValue}>{message.Calculations[key]}</td>
                                        </tr>
                                    )
                                })
                            }
                        </>
                    }
                    {/* Query Filters */}
                    {Object.keys(queryFilters ?? {}).length > 0 &&
                        <>
                            <tr>
                                <td className={`${classes.qryFilterHeading} qryFilterHeading`} colSpan={2}>{t('filters_applied')}:</td>
                            </tr>
                            {
                                Object.keys(message.query_filters).map(filterKey => {
                                    return (
                                        <tr key={filterKey}>
                                            <td className={classes.qryFilterName}>{filterKey.replaceAll("_", " ")}:</td>
                                            <td className={classes.qryFilterValue}>{message.query_filters[filterKey]?.join(", ")}</td>
                                        </tr>
                                    )
                                })
                            }
                        </>
                    }

                </table>
            })
        }

        return accordionList;
    }

    return (
        <div id={`response-${questionUid}`} data-testid="result-component">
            {message && Object.keys(message).length > 0 && (
                <>
                    <div className={`${classes.msgActions} qstnActions`}>
                        <button
                            className={classes.msgActionBtn}
                            title={t('edit')}
                            onClick={editMessage}
                            data-testid="edit-msg-button"
                        >
                            <img src={EditIcon} alt={t('edit')} />
                        </button>
                        <button
                            className={classes.msgActionBtn}
                            title={t('resend')}
                            onClick={resendMessage}
                            data-testid="resend-msg-button"
                        >
                            <img src={RetryIcon} alt={t('resend')} />
                        </button>
                    </div>
                    <div data-testid="chat-message" className={`${classes.botMessage} rcb-bot-message-entry rcb-bot-message`}>
                        <h4 className='response-title'>LISA:</h4>
                        <div className={classes.finalAnswer}>
                            <ReactMarkdown>
                                {message?.final_answer}
                            </ReactMarkdown>
                        </div>

                        {/* <p className={classes.confidenceLabel}>
                            <small>
                                <b>Confidence: </b>
                                <span className={`${classes.confidenceLegend} confidenceHigh`}></span>
                                <span> High (88.7%)</span>
                            </small>
                        </p> */}

                        {/* Accordions for filter details */}
                        <ChatAccordion data={getAccordionItems()} />

                        {rest?.duration && <div className={classes.responseTime}>
                            {`${(parseInt(rest?.duration) / 1000).toFixed(2)} ${t('seconds')}`}
                        </div>}

                        {/* Action item on hover of message (like, unlike, copy, etc) */}
                        <div className={classes.noHeight}>
                            <div className={`${classes.msgActions} replyActions`}>
                                <button
                                    className={`${classes.msgActionBtn} ${feedbackFlag === 1 ? 'active' : ''}`}
                                    title={t('like')}
                                    onClick={likeMessage}
                                    data-testid="like-msg-button"
                                >
                                    <img src={feedbackFlag === 1 ? LikeIconFilled : LikeIcon} alt={t('like')} />
                                </button>
                                <button
                                    className={`${classes.msgActionBtn} ${feedbackFlag === -1 ? 'active' : ''}`}
                                    title={t('unlike')}
                                    onClick={unlikeMessage}
                                    data-testid="unlike-msg-button"
                                >
                                    <img src={feedbackFlag === -1 ? UnlikeIconFilled : UnlikeIcon} alt={t('unlike')} />
                                </button>
                                <button
                                    className={classes.msgActionBtn}
                                    title={t('copy')}
                                    onClick={copyMessage}
                                    data-testid="copy-msg-button"
                                >
                                    <img src={CopyIcon} alt={t('copy')} />
                                </button>
                                {isMsgCopied && <b>Copied!</b>}
                            </div>
                            {/* Feedback Dialog */}
                            <ChatFeedback isOpen={isFeedbackOpen} onClose={closeFeedback} onSubmit={submitFeedback} />
                        </div>
                    </div>
                </>)}
            {tableData && tableData.length && (
                <>
                    <TableContainer className={classes.tableContainer}>
                        <Table className={classes.table} data-testid="chat-table">
                            <TableHead className={classes.tableHead}>
                                <TableRow>
                                    {dataKeys.map((v) => {
                                        return (
                                            <TableCell className={`${classes.tableCellHeader} tableCellHeader`} key={v}>
                                                {v.replaceAll('_', ' ')}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableData.map((dataObj, index) => (
                                    <TableRow key={'row_' + index}>
                                        {dataKeys.map((v, i) => {
                                            const alignmentClass = isCurrencyField(v, dataObj[v]) ? ' MuiTableCell-alignRight' : ''
                                            return (
                                                <TableCell
                                                    key={'row_' + index + '_col_' + i}
                                                    className={classes.tableCell + alignmentClass}
                                                    scope="row">
                                                    {formatValue(v, dataObj[v])}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <div className={`${classes.tableFooterExt} tableFooterExt`}>
                        {`(${tableData.length} ${tableData.length == 1 ? t('record') : t('records')})`}
                    </div>
                </>
            )}
            {(tableData?.length || (chartProps && chartProps?.is_chart_applicable)) && (
                <ChatbotKpiWrapper {...chartProps} {...rest} tableData={tableData} dataKeys={dataKeys} message={message} />
            )}
        </div>
    );
};

